import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import {
  getSetupState,
  initializeSetupState,
  updateStepStatus,
  getCurrentStep,
  getCompletedStepsCount,
  isSetupCompleted,
  areAllStepsCompleted,
  finalizeSetup,
  SetupState,
  TOTAL_SETUP_STEPS,
  isSetupAllowedForUser,
} from 'utils/setupStorage'

/**
 * Setup Context Type Definition
 * Manages the onboarding/setup flow state and actions
 */
interface SetupContextType {
  // State
  isDrawerOpen: boolean
  isDrawerMinimized: boolean
  isModalOpen: boolean
  currentStep: number
  completedSteps: number
  totalSteps: number
  setupState: SetupState | null
  isSetupComplete: boolean
  areAllStepsCompleted: boolean

  // Actions
  openDrawer: () => void
  closeDrawer: () => void
  minimizeDrawer: () => void
  maximizeDrawer: () => void
  toggleDrawer: () => void
  goToStep: (step: number) => void
  completeStep: (stepId: number) => void
  skipStep: (stepId: number) => void
  refreshSetupState: () => void
  isStepCompleted: (stepId: number) => boolean
  setCurrentStep: (stepId: number) => void
  setModalOpen: (isOpen: boolean) => void
  finalizeSetup: () => void
}

const SetupContext = createContext<SetupContextType | undefined>(undefined)

/**
 * Hook to access setup context
 * @throws Error if used outside SetupProvider
 */
export const useSetup = () => {
  const context = useContext(SetupContext)
  if (!context) {
    throw new Error('useSetup must be used within SetupProvider')
  }
  return context
}

interface SetupProviderProps {
  children: ReactNode
}

/**
 * Setup Provider Component
 * Manages the onboarding flow state for the entire application
 */
export const SetupProvider: React.FC<SetupProviderProps> = ({ children }) => {
  const uid = useSelector((state: RootState) => state.auth.uid)
  const openCompletionTimeoutRef = useRef<number | null>(null)

  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isDrawerMinimized, setIsDrawerMinimized] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Setup progress state
  const [setupState, setSetupState] = useState<SetupState | null>(null)
  const [currentStep, setCurrentStep] = useState(1)
  const [completedSteps, setCompletedSteps] = useState(0)
  const [isSetupComplete, setIsSetupComplete] = useState(false)
  const [allStepsCompleted, setAllStepsCompleted] = useState(false)

  const totalSteps = TOTAL_SETUP_STEPS

  /**
   * Refreshes the setup state from localStorage
   * Handles initialization, completion detection, and state updates
   */
  const refreshSetupState = useCallback(() => {
    if (!uid) return

    // Check if user is allowed to access setup feature
    if (!isSetupAllowedForUser(uid)) {
      // Reset state for non-allowed users
      setSetupState(null)
      setCurrentStep(1)
      setCompletedSteps(0)
      setIsSetupComplete(false)
      return
    }

    let state = getSetupState(uid)

    // Initialize setup state for new users
    if (!state) {
      state = initializeSetupState(uid)
    }

    const completed = isSetupCompleted(uid)
    const allCompleted = areAllStepsCompleted(uid)

    // Handle setup completion
    if (completed) {
      console.log('✅ Setup finalized for user:', uid)
      setIsDrawerMinimized(true)
    } else if (allCompleted) {
      // All steps completed but not finalized - show completion screen
      console.log('🎉 All steps completed, scheduling completion drawer open for user:', uid)
      // Keep minimized for now; we'll open it with a delay in the effect below
      setIsDrawerMinimized(true)
    } else {
      // Drawer always starts minimized until user completes a step (clicks Activate)
      console.log('🔒 Drawer minimized for user:', uid)
      setIsDrawerMinimized(true)
    }

    setSetupState(state)
    setCurrentStep(getCurrentStep(uid))
    setCompletedSteps(getCompletedStepsCount(uid))
    setIsSetupComplete(completed)
    setAllStepsCompleted(allCompleted)
  }, [uid])

  useEffect(() => {
    refreshSetupState()
  }, [refreshSetupState])

  /**
   * Delay opening the drawer by 5 seconds ONLY when all steps are completed (but not finalized).
   */
  useEffect(() => {
    // cleanup any previous timers
    if (openCompletionTimeoutRef.current) {
      window.clearTimeout(openCompletionTimeoutRef.current)
      openCompletionTimeoutRef.current = null
    }

    if (!uid) return
    if (isSetupComplete) return
    if (!allStepsCompleted) return

    openCompletionTimeoutRef.current = window.setTimeout(() => {
      setIsDrawerMinimized(false)
      openCompletionTimeoutRef.current = null
    }, 5000)

    return () => {
      if (openCompletionTimeoutRef.current) {
        window.clearTimeout(openCompletionTimeoutRef.current)
        openCompletionTimeoutRef.current = null
      }
    }
  }, [allStepsCompleted, isSetupComplete, uid])

  // Drawer actions
  const openDrawer = useCallback(() => {
    setIsDrawerOpen(true)
    setIsDrawerMinimized(false)
  }, [])

  const closeDrawer = useCallback(() => {
    setIsDrawerMinimized(true)
  }, [])

  const minimizeDrawer = useCallback(() => {
    setIsDrawerMinimized(true)
  }, [])

  const maximizeDrawer = useCallback(() => {
    setIsDrawerMinimized(false)
  }, [])

  const toggleDrawer = useCallback(() => {
    setIsDrawerMinimized((prev) => !prev)
  }, [])

  const setModalOpen = useCallback((isOpen: boolean) => {
    setIsModalOpen(isOpen)
  }, [])

  // Step navigation
  const goToStep = useCallback((step: number) => {
    setCurrentStep(step)
  }, [])

  /**
   * Marks a step as completed and refreshes state
   */
  const completeStep = useCallback(
    (stepId: number) => {
      if (!uid) return
      updateStepStatus(uid, stepId, 'completed')
      refreshSetupState()
    },
    [uid, refreshSetupState]
  )

  /**
   * Skips a step and refreshes state
   */
  const skipStep = useCallback(
    (stepId: number) => {
      if (!uid) return
      updateStepStatus(uid, stepId, 'skipped')
      refreshSetupState()
    },
    [uid, refreshSetupState]
  )

  const isStepCompleted = useCallback(
    (stepId: number) => {
      if (!setupState) return false
      return setupState.steps[stepId]?.status === 'completed'
    },
    [setupState]
  )

  const handleFinalizeSetup = useCallback(() => {
    if (!uid) return
    finalizeSetup(uid)
    refreshSetupState()
  }, [uid, refreshSetupState])

  const value: SetupContextType = {
    isDrawerOpen,
    isDrawerMinimized,
    isModalOpen,
    currentStep,
    completedSteps,
    totalSteps,
    setupState,
    isSetupComplete,
    areAllStepsCompleted: allStepsCompleted,
    isStepCompleted,
    openDrawer,
    closeDrawer,
    minimizeDrawer,
    maximizeDrawer,
    toggleDrawer,
    goToStep,
    completeStep,
    skipStep,
    refreshSetupState,
    setCurrentStep,
    setModalOpen,
    finalizeSetup: handleFinalizeSetup,
  }

  return <SetupContext.Provider value={value}>{children}</SetupContext.Provider>
}
